'use client';
import { UserLocale } from '@mnd-frontend/graphql-schema';
import type { FC } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { TranslationFunction } from '../types';

interface TranslationContextType {
  t: TranslationFunction;
  Trans: React.ComponentType<{ i18nKey: string; [key: string]: any }>;
  lng: UserLocale;
  loading?: boolean;
}

const defaultValue = {
  t: () => '',
  Trans: ({ i18nKey }: { i18nKey: string }) => i18nKey,
  lng: UserLocale.En,
};

const wrapTranslationFunction = (t: TranslationFunction, lng: UserLocale): TranslationFunction => {
  return (key: string, props: Record<string, any> = {}) => {
    return t(key, { lng, ...props });
  };
};

const ReportTranslationContext = createContext<TranslationContextType>(defaultValue);

export const ReportTranslationContextProvider: FC<
  TranslationContextType & { children: React.ReactNode }
> = ({ children, t, lng, Trans, loading }) => {
  const wrappedT = useMemo(
    () => (loading ? () => '' : wrapTranslationFunction(t, lng)),
    [t, lng, loading],
  );

  const value = useMemo(
    () => ({
      Trans,
      t: wrappedT,
      lng,
    }),
    [wrappedT, lng, Trans],
  );

  return (
    <ReportTranslationContext.Provider value={value}>{children}</ReportTranslationContext.Provider>
  );
};

export const useReportTranslation = () => {
  const context = useContext(ReportTranslationContext);
  if (context === defaultValue) {
    throw new Error('useReportTranslation must be used within a ReportTranslationContextProvider');
  }
  return context;
};
